<template>
  <div class="flex-1 flex flex-col items-center gap-y-8">
    <div class="flex-1 w-full xl:w-4/6 flex justify-between flex-col md:flex-row gap-8">
      <form action="" class="w-full flex flex-col gap-y-4" @submit.prevent="onSubmit">
        <TextInput
          :show-error-spacer="false"
          :label="$t('IdentityNumber')"
          :rules="[requiredRule]"
          :default-value="props.data.eid"
          direction="flex-col justify-start"
          label-class="w-full"
          is-read-only
          field-name="eid"
          is-required
          placeholder="###-####-#######-#"
          mask="###-####-#######-#"
          class="w-full md:w-[50%]"
        ></TextInput>
        <div class="md:py-12 w-full flex flex-col md:flex-row gap-2">
          <FilledButton
            :key="choice"
            :fill-class="[choice == selectedChoice ? 'bg-primary text-white' : 'border-primary']"
            v-for="choice in choices"
            class="flex-1 flex justify-center items-center gap-x-3 border rounded-md py-1.5 px-2"
            @click="selectedChoice = choice"
          >
            <faCheck v-if="choice == selectedChoice" class="w-3 fill-white" />
            <span :class="[selectedChoice == choice ? 'text-white' : 'text-primary']">
              {{ $t(choice) }}
            </span>
          </FilledButton>
        </div>
        <div
          v-if="selectedChoice == 'ByRegisterdMobileNumbers'"
          class="w-full flex flex-col gap-y-4"
        >
          <template v-if="!isOtpSent">
            <HeaderTypo as="h4" class="text-lg">{{ $t('SelectMobileNumber') }}</HeaderTypo>
            <FilledButton
              v-for="mobile in registeredNumbers"
              :key="mobile"
              :fill-class="[selectedMobile == mobile ? 'bg-primary text-white' : 'border-primary']"
              class="md:max-w-sm flex-1 flex justify-center items-center gap-x-3 border rounded-md py-1 px-2"
              @click="selectedMobile = mobile"
            >
              <faCheck v-if="selectedMobile == mobile" class="w-3 fill-white" />
              <span
                :class="[selectedMobile == mobile ? 'text-white' : 'text-primary']"
                class="text-lg ltr:font-sans"
              >
                {{ mobile.mobileNumber }}
              </span>
            </FilledButton>
          </template>
          <div v-else class="flex flex-col gap-y-2">
            <TextInput
              :show-error-spacer="false"
              :label="$t('EnterOTPLabel')"
              :rules="[requiredRule, maxRule(6)]"
              direction="flex-col justify-start"
              label-class="w-full"
              field-name="OneTimePassword"
              is-required
              class="w-full md:w-[50%]"
            ></TextInput>
            <div class="flex gap-x-2 text-brand-black-secondary">
              {{ $t('ReSendOTPTextWithTime') }}
              <div class="text-red-600">{{ otpCount }} {{ $t('Second') }}</div>
            </div>
            <button
              class="w-fit rounded-md bg-primary/80 px-3 py-1.5 flex items-center gap-x-2"
              :disabled="!isCanResendOtp"
              :class="[!isCanResendOtp && 'cursor-not-allowed bg-gray-400/40']"
              @click="reSendOtp"
            >
              <span class="ltr:font-sans tracking-wide flex-1 text-white text-sm pb-[1px]">
                {{ $t('ReSendOTPBtn') }}
              </span>
            </button>
          </div>
        </div>
        <div
          v-if="selectedChoice == 'ByProvidingPersonalInfo'"
          class="w-full flex flex-col gap-y-4"
        >
          <div class="w-full flex flex-col md:flex-row justify-between gap-4">
            <TextInput
              :show-error-spacer="false"
              :label="$t('passportNumber')"
              :rules="[requiredRule, maxRule(20)]"
              direction="flex-col justify-start"
              label-class="w-full"
              field-name="passportNo"
              is-required
              class="flex-1"
            ></TextInput>
            <TextInput
              :show-error-spacer="false"
              :label="$t('IdentityCardNumber')"
              :rules="[requiredRule, maxRule(20)]"
              direction="flex-col justify-start"
              label-class="w-full"
              field-name="identityCardNumber"
              is-required
              class="flex-1"
            ></TextInput>
          </div>
          <div class="w-full flex flex-col md:flex-row justify-between gap-4">
            <DatepickerCalendar
              :show-error-spacer="false"
              :label="$t('DateOfBirth')"
              :rules="[requiredRule, minDateRule]"
              :max-date="new Date()"
              label-class="w-full"
              direction="flex-col justify-start"
              field-name="dateOfBirth"
              is-required
              class="flex-1"
            ></DatepickerCalendar>
            <div class="flex-1 p-1"></div>
          </div>
          <div class="w-full flex flex-col md:flex-row justify-between gap-4">
            <DatepickerCalendar
              :show-error-spacer="false"
              :label="$t('PassportIssueDate')"
              :rules="[requiredRule, minIssueDateRule, maxIssueDateRule]"
              label-class="w-full"
              direction="flex-col justify-start"
              field-name="passportIssueDate"
              is-required
              class="flex-1"
            ></DatepickerCalendar>
            <DatepickerCalendar
              :show-error-spacer="false"
              :label="$t('PassportExpiryDate')"
              :rules="[requiredRule, minExpiryDate]"
              label-class="w-full"
              direction="flex-col justify-start"
              field-name="passportExpiryDate"
              is-required
              class="flex-1"
            ></DatepickerCalendar>
          </div>
        </div>
      </form>
    </div>
    <div class="w-full">
      <FormFooter @onBack="$emit('onBack')" @onNext="submitForm"></FormFooter>
    </div>
  </div>
</template>

<script setup>
import faCheck from '@/assets/icons/faCheck.svg?component'
import FilledButton from '@/components/Buttons/Filled.vue'
import DatepickerCalendar from '@/components/Form/DatepickerCalendar.vue'
import TextInput from '@/components/Form/TextInput.vue'
import FormFooter from '@/components/Onboarding/FormFooter.vue'
import { useForm } from 'vee-validate'
import { computed, ref } from 'vue'
import { notify } from 'notiwind'
import axios from '@/services'
import { useI18n } from 'vue-i18n'
import { DateTime } from 'luxon'
import { useFormStore } from '@/stores/form'
import useCountdown from '@/composables/useCountdown'
import lookups from '@/assets/json/clientLookups.json'

const emit = defineEmits(['onNext'])
const i18n = useI18n()
const formStore = useFormStore()
const { time: otpCount, start: startOtp } = useCountdown()

const choices = ['ByRegisterdMobileNumbers', 'ByProvidingPersonalInfo']

const selectedChoice = ref('ByProvidingPersonalInfo')
const selectedMobile = ref()
const isOtpSent = ref()

const requiredRule = ref({
  name: 'required',
  message: 'INVALID_REQUIRED'
})
const maxRule = (count) => {
  return {
    name: 'max_char',
    value: count,
    message: 'INVALID_MAX_CHAR'
  }
}
const minDateRule = ref({
  name: 'max_date',
  value: new Date(),
  message: 'INVALID_MIN_DATE'
})
const minIssueDateRule = ref({
  name: 'min_date',
  ref: 'dateOfBirth',
  message: 'INVALID_MIN_DATE'
})
const maxIssueDateRule = ref({
  name: 'max_date',
  ref: 'passportExpiryDate',
  message: 'INVALID_MIN_DATE'
})
const minExpiryDate = ref({
  name: 'min_date',
  ref: 'passportIssueDate',
  message: 'INVALID_MIN_DATE'
})

const props = defineProps({
  data: Object
})

const registeredNumbers = computed(() => {
  return props.data?.results.registerdInformation?.registeredNumbers
})

const verficationType = computed(() => {
  return selectedChoice.value === 'ByRegisterdMobileNumbers'
    ? lookups.verficationTypes.OneTimePassword
    : lookups.verficationTypes.personalInformation
})

const isCanResendOtp = computed(() => {
  return otpCount.value == 0
})

const reSendOtp = async () => {
  isOtpSent.value = true
  startOtp(60)
  await sendOtp()
}

const formateDate = (value) => {
  return DateTime.fromMillis(value).toFormat('yyyy/MM/dd')
}

formStore.$reset()
const form = useForm({
  initialValues: {}
})
formStore.form = form

const submitForm = async () => {
  if (selectedChoice.value == 'ByProvidingPersonalInfo') {
    form.setTouched(true)
    const validationResult = await form.validate()
    if (!validationResult.valid) return
    await validatePersonalInfo()
    // emit('onNext')
    // TODO: submit this form
    // if all succeeds, emit('onNext') to go to next step

    return
  } else {
    // TODO: send otp, validate that otp, and emit('onNext')
    // user clicked next to send otp
    if (!isOtpSent.value) {
      startOtp(60)
      await sendOtp()
      isOtpSent.value = true
    } else {
      await validateOtp()
    }
  }
}

const sendOtp = async () => {
  const eid = props.data.eid.replaceAll('-', '')
  const url = `/api/user/individual/identityNumber/${eid}/mobile/1/sendOTP`

  try {
    const res = await axios.get(url)
  } catch (error) {
    console.error('Error sending otp', error)
    return
  }
}

const validateOtp = async () => {
  form.setTouched(true)
  const validationResult = await form.validate()
  if (!validationResult.valid) return
  // user clicked next to validate otp
  const eid = props.data.eid.replaceAll('-', '')
  const otp = form.values.OneTimePassword

  const url = `/api/user/individual/identityNumber/${eid}/mobile/1/otp/${otp}/validate`
  try {
    const response = await axios.get(url)

    if (!response?.isValid) {
      notify(
        {
          group: 'error',
          title: i18n.t('InvalidOTPInputTitle'),
          text: i18n.t('InvalidOTPInputBody')
        },
        6000
      )
    }

    emit('onNext', response)
  } catch (error) {
    return
  }
}

const validatePersonalInfo = async () => {
  // user clicked next to validate otp
  const url = `/api/user/individual/identityNumber/${form.values.identityNumber}/validatePersonalInfo`
  const data = {
    verficationType: verficationType.value,
    identityCardNumber: form.values.identityCardNumber,
    passportExpiryDate: formateDate(form.values.passportExpiryDate),
    passportIssueDate: formateDate(form.values.passportIssueDate),
    dateOfBirth: formateDate(form.values.dateOfBirth)
  }
  const res = { ...form.values }
  try {
    const response = await axios.post(url, data)

    if (!response?.isValid) {
      notify(
        {
          group: 'error',
          title: i18n.t('InvalidOTPInputTitle'),
          text: i18n.t('InvalidOTPInputBody')
        },
        6000
      )
    }

    // emit('onNext', response)
    // DEBUG ONLY
    emit('onNext', {
      isValid: true,
      ...res
    })

    // TODO: success
  } catch (error) {
    return
  }
}
</script>

<style lang="scss" scoped></style>
